import moment from 'moment';

export const getRowValueByKeys = (row = {}, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '';
};

export const escapeCsvValue = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const buildReportCsv = (rows = [], columns = []) => {
  const headerRow = columns.map((column) => escapeCsvValue(column.label)).join(',');
  const dataRows = rows.map((row) =>
    columns
      .map((column) => escapeCsvValue(getRowValueByKeys(row, column.keys)))
      .join(','),
  );

  return ['\uFEFF' + headerRow, ...dataRows].join('\r\n');
};

export const triggerCsvDownload = (csvContent, fileName) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getSafeReportFileNameBase = (title = '', fallback = 'report') => {
  return (
    String(title || fallback)
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'report'
  );
};

export const ensureFileExtension = (fileName, extension) => {
  const sanitized = (fileName || '').trim() || `report-${Date.now()}`;
  const hasExtension = new RegExp(`\\.${extension}$`, 'i').test(sanitized);
  return hasExtension ? sanitized : `${sanitized}.${extension}`;
};

const EXCLUDED_RAW_KEYS = new Set(['__v', 'agent', 'invoice']);
const MAX_PDF_COLUMN_WIDTH_PT = 337.5; // 450px in PDF points

const withBreakableLongTokens = (value) => {
  const text = String(value ?? '');
  // Insert break opportunities for long unspaced tokens (URLs/IDs/emails).
  return text.replace(/(\S{18})(?=\S)/g, '$1 ');
};

const humanizeRawColumnKey = (key = '') => {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) =>
      word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word,
    )
    .join(' ');
};

const normalizeRawCellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const isoDate = moment(value, moment.ISO_8601, true);
    if (isoDate.isValid()) {
      return isoDate.format('M/D/YYYY');
    }
    return value;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format('M/D/YYYY');
  }
  return value;
};

export const buildRawReportFromFullData = (fullData = []) => {
  const normalizedRows = Array.isArray(fullData)
    ? fullData.map((row) => {
        const nextRow = {};

        Object.entries(row || {}).forEach(([key, value]) => {
          if (EXCLUDED_RAW_KEYS.has(key)) return;
          if (Array.isArray(value)) return;
          if (typeof value === 'boolean') return;
          if (value && typeof value === 'object' && !(value instanceof Date))
            return;
          nextRow[key] = normalizeRawCellValue(value);
        });

        return nextRow;
      })
    : [];

  const orderedKeys = [];
  normalizedRows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!orderedKeys.includes(key)) {
        orderedKeys.push(key);
      }
    });
  });

  const columns = orderedKeys.map((key) => ({
    label: humanizeRawColumnKey(key),
    keys: [key],
  }));

  return {
    rows: normalizedRows,
    columns,
  };
};

export const triggerPdfDownloadFromRows = async ({
  rows = [],
  fileName,
  title,
  columns = [],
}) => {
  const [{ jsPDF }] = await Promise.all([import('jspdf')]);

  const margin = 24;
  const lineHeight = 12;
  const cellPadding = 4;
  const basePageWidth = 842;
  const pageHeight = 595;
  const measurementDoc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  });
  const safeTitle = title || 'report';
  const measureCellWidth = (value, isHeader = false) => {
    const text = withBreakableLongTokens(value);
    const fontSize = isHeader ? 8.5 : 8;
    measurementDoc.setFont('helvetica', isHeader ? 'bold' : 'normal');
    measurementDoc.setFontSize(fontSize);
    return measurementDoc.getTextWidth(text);
  };

  const getDynamicMaxColumnWidth = (totalColumns) => {
    const usableWidth = basePageWidth - margin * 2;
    const perColumnCap = usableWidth / Math.max(3, Math.min(totalColumns || 1, 6));
    return Math.min(MAX_PDF_COLUMN_WIDTH_PT, perColumnCap);
  };

  const tableColumnWidths = columns.map((column, columnIndex) => {
    const headerWidth = measureCellWidth(column.label, true);
    const sampleWidths = (rows || []).map((row) =>
      measureCellWidth(getRowValueByKeys(row, column.keys), false),
    );
    const longestCellWidth = Math.max(headerWidth, ...sampleWidths, 40);
    const preferredWidth = longestCellWidth + cellPadding * 2 + 18;
    const minWidth = columnIndex === 0 ? 90 : 80;
    return Math.min(
      Math.max(preferredWidth, minWidth),
      getDynamicMaxColumnWidth(columns.length),
    );
  });

  const tableWidth = tableColumnWidths.reduce((sum, width) => sum + width, 0);
  const pageWidth = Math.max(basePageWidth, tableWidth + margin * 2);
  const document = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: [pageWidth, pageHeight],
  });

  const maxWidth = pageWidth - margin * 2;

  let y = margin;
  document.setFont('helvetica', 'bold');
  document.setFontSize(14);
  const titleLines = document.splitTextToSize(safeTitle, maxWidth);
  titleLines.forEach((line) => {
    document.text(line, margin, y);
    y += lineHeight;
  });

  document.setFont('helvetica', 'normal');
  document.setFontSize(10);
  document.text(`Generated: ${moment().format('YYYY-MM-DD HH:mm')}`, margin, y);
  y += lineHeight * 1.5;

  const getRowHeight = (cells = []) => {
    const wrappedCells = cells.map((cell, index) =>
      document.splitTextToSize(
        withBreakableLongTokens(cell),
        Math.max(tableColumnWidths[index] - cellPadding * 2, 24),
      ),
    );
    const maxLines = Math.max(
      1,
      ...wrappedCells.map((cellLines) => cellLines.length),
    );
    return maxLines * lineHeight + cellPadding * 2;
  };

  const drawRow = (cells = [], rowY, isHeader = false) => {
    document.setFont('helvetica', isHeader ? 'bold' : 'normal');
    document.setFontSize(8.5);

    const wrappedCells = cells.map((cell, index) =>
      document.splitTextToSize(
        withBreakableLongTokens(cell),
        Math.max(tableColumnWidths[index] - cellPadding * 2, 24),
      ),
    );

    const maxLines = Math.max(
      1,
      ...wrappedCells.map((cellLines) => cellLines.length),
    );
    const rowHeight = maxLines * lineHeight + cellPadding * 2;

    wrappedCells.forEach((cellLines, index) => {
      const x =
        margin +
        tableColumnWidths.slice(0, index).reduce((sum, width) => sum + width, 0);
      const cellWidth = tableColumnWidths[index];
      document.rect(x, rowY, cellWidth, rowHeight);
      document.text(cellLines, x + cellPadding, rowY + cellPadding + lineHeight * 0.8);
    });

    return rowHeight;
  };

  const headerCells = columns.map((column) => column.label);
  const drawHeader = () => {
    const headerHeight = drawRow(headerCells, y, true);
    y += headerHeight;
  };

  drawHeader();

  (rows || []).forEach((row) => {
    const rowCells = columns.map((column) =>
      String(getRowValueByKeys(row, column.keys) ?? ''),
    );

    const requiredHeight = getRowHeight(rowCells);
    if (y + requiredHeight > pageHeight - margin) {
      document.addPage();
      y = margin;
      drawHeader();
    }

    const rowHeight = drawRow(rowCells, y);
    y += rowHeight;
  });

  document.save(ensureFileExtension(fileName, 'pdf'));
};